import { Fragment, FunctionComponent, h } from 'preact'
import { MutableRef, useEffect, useMemo } from 'preact/hooks'
import cls from 'classnames'

import {
  EndingField as EndingFieldType,
  FeedbackFieldProps,
  Field,
  FormFieldType,
  RatingField as RatingFieldType,
  TextArea,
} from 'scripts/type'
import { useParentWindowSize, useBrandingProps, usePostHeightChangeMessage } from 'scripts/hooks'
import { Branding, FormContainer, SafeArea } from 'components/ui'

import { EndingField, RatingField, TextareaField } from './Fields'
import useRefs from './useRefs'
import styles from './index.module.scss'

export type FeedbackFormPropsProps = FeedbackFieldProps & {
  onFieldChange?: (height: number) => void
  field: Field
  onClose?: () => void
}

const FeedbackForm: FunctionComponent<FeedbackFormPropsProps> = (props) => {
  const { windowSizeSm } = useParentWindowSize()
  const {
    field,
    style,
    ratingField,
    textareaField,
    onSubmit,
    onRatingFieldChange,
    onTextAreaFieldChange,
    onFieldChange,
    onClose,
    onFieldHeightChange,
  } = props
  const { contentRef, starFieldRef, brandingRef, safeAreaRef, textareaRef } = useRefs()

  const showTextareaField = useMemo(() => field.type === FormFieldType.TextArea && !field.hidden, [field])
  const showEnding = useMemo(() => field.type === FormFieldType.EndingField, [field])

  const { postHeightChange } = usePostHeightChangeMessage(
    contentRef as MutableRef<HTMLDivElement>,
    brandingRef as MutableRef<HTMLDivElement>,
    32
  )
  const { brandingUrl, brandingVisible } = useBrandingProps()

  useEffect(() => {
    setTimeout(() => {
      if (field.type !== FormFieldType.EndingField) {
        starFieldRef.current?.fadeIn()
      }
    }, 700)
  }, [field.type])

  useEffect(() => {
    const widgetHeight = postHeightChange(safeAreaRef?.current?.getHeight() ?? 0)
    onFieldHeightChange?.(widgetHeight)
    onFieldChange?.(widgetHeight)
  }, [field])

  return (
    <FormContainer onClose={onClose} closeStyle={{ top: '12px', insetInlineEnd: '12px' }} style={style} type="feedback">
      <div className={styles.root}>
        <div className={styles.main}>
          <div
            className={cls(styles.content, {
              [styles.contentSm]: windowSizeSm,
              [styles.contentPbWithBranding]: field.type === FormFieldType.RatingField && brandingVisible,
              [styles.contentPbNoBranding]: field.type === FormFieldType.RatingField && !brandingVisible,
            })}
            ref={contentRef}
          >
            {!showEnding ? (
              <Fragment>
                {
                  <RatingField
                    field={ratingField as RatingFieldType}
                    onChange={onRatingFieldChange}
                    ref={starFieldRef}
                  />
                }
                {showTextareaField && (
                  <TextareaField
                    ref={textareaRef}
                    field={textareaField as TextArea}
                    onChange={onTextAreaFieldChange}
                    onSubmit={onSubmit}
                  />
                )}
              </Fragment>
            ) : (
              <EndingField field={field as EndingFieldType} />
            )}
          </div>
        </div>
        {brandingVisible && <Branding brandingUrl={brandingUrl!} ref={brandingRef} />}
        <SafeArea ref={safeAreaRef} />
      </div>
    </FormContainer>
  )
}

export default FeedbackForm
